/**
 * useRecentAreas Hook
 *
 * Manages recently viewed areas in localStorage. Automatically deduplicates
 * and keeps the 10 most recent areas.
 *
 * @returns Object with recentAreas array and methods to add/clear areas
 *
 * @example
 * ```tsx
 * const { recentAreas, addRecentArea, clearRecentAreas } = useRecentAreas();
 *
 * // Add an area when user views it
 * addRecentArea({
 *   resourceId: 'berkeley-ca',
 *   name: 'Berkeley',
 *   city: 'Berkeley',
 *   state: 'CA'
 * });
 * ```
 */

import { useCallback } from 'react';
import type { AutocompleteResult } from '@/src/lib/api';
import { useLocalStorage } from '@/src/lib/hooks/utils';

const MAX_RECENT_AREAS = 10;
const STORAGE_KEY = 'hotpads_recent_areas';

export type RecentArea = AutocompleteResult & {
  viewedAt?: number; // Timestamp when area was viewed
};

export function useRecentAreas() {
  const [recentAreas, setRecentAreas] = useLocalStorage<Array<RecentArea>>(STORAGE_KEY, []);

  // Add an area to recent areas (or update its timestamp if already exists)
  const addRecentArea = useCallback(
    (area: AutocompleteResult) => {
      setRecentAreas((prev) => {
        // Remove the area if it already exists (we'll add it to the front)
        const filtered = prev.filter((item) => item.resourceId !== area.resourceId);

        // Add the new area with current timestamp at the front
        const updated: Array<RecentArea> = [
          {
            ...area,
            viewedAt: Date.now(),
          },
          ...filtered,
        ];

        // Keep only the most recent MAX_RECENT_AREAS
        return updated.slice(0, MAX_RECENT_AREAS);
      });
    },
    [setRecentAreas],
  );

  // Clear all recent areas
  const clearRecentAreas = useCallback(() => {
    setRecentAreas([]);
  }, [setRecentAreas]);

  return {
    recentAreas,
    addRecentArea,
    clearRecentAreas,
  };
}
